import { businessQuotesService } from '@/services/quotes/business';
import type {
  GetQuoteDocumentResponse,
  QuoteDocumentType,
} from '@/services/quotes/types';
import { STALE_TIME_FIVE_MINUTES_MS } from '@/shared/constants/query-stale-time.constants';
import { useQuery } from '@tanstack/react-query';

type UseQuoteDocumentOptions = {
  /** Quando false não dispara a query (útil pra esconder a carta antes da recusa). */
  enabled?: boolean;
};

/**
 * GET /portal/v1/business/quotes/:policyId/documents?type=<type>.
 * `type=refusal_letter` retorna 404 enquanto o template `analysis_refusal` não
 * estiver cadastrado ou enquanto a cotação não tiver sido recusada — passe
 * `enabled: false` nesses casos para evitar barulho no console.
 */
export function useQuoteDocument(
  policyId: string | undefined,
  type: QuoteDocumentType = 'quote',
  options: UseQuoteDocumentOptions = {}
) {
  const { enabled = true } = options;

  return useQuery<GetQuoteDocumentResponse>({
    queryKey: ['quote-document', policyId, type],
    queryFn: async () => {
      if (!policyId) {
        throw new Error('PolicyId é obrigatório');
      }

      const response = await businessQuotesService.getDocument(policyId, type);
      if (!response.success || !response.data) {
        throw new Error(
          response.error ||
            (type === 'refusal_letter'
              ? 'Erro ao carregar carta de recusa'
              : 'Erro ao carregar documento da cotação')
        );
      }

      return response.data;
    },
    staleTime: STALE_TIME_FIVE_MINUTES_MS,
    enabled: !!policyId && enabled,
    retry: false,
  });
}
